/**
 * Envelope encryption using AES-256-GCM via Web Crypto API.
 *
 * Format: "enc:v1:<iv1_b64>:<ciphertext_b64>:<iv2_b64>:<wrappedDek_b64>"
 *
 * Each secret gets its own DEK (Data Encryption Key), wrapped by the master key.
 * Key rotation only re-wraps DEKs — no need to re-encrypt every secret.
 */

const ENVELOPE_PREFIX = 'enc:v1:';

function base64Encode(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64Decode(str: string): Uint8Array {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function importKey(rawKeyBase64: string): Promise<CryptoKey> {
  const keyBytes = base64Decode(rawKeyBase64);
  return crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function aesEncrypt(key: CryptoKey, iv: Uint8Array, plaintext: Uint8Array): Promise<ArrayBuffer> {
  return crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource);
}

async function aesDecrypt(key: CryptoKey, iv: Uint8Array, ciphertext: Uint8Array): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, ciphertext as BufferSource);
}

/**
 * Generate a cryptographically secure secret (32 bytes, base64url encoded).
 * Use this instead of uuidv7() or crypto.randomUUID() for secrets.
 */
export function generateSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Encode(bytes.buffer);
}

/**
 * Encrypt a plaintext string using envelope encryption (AES-256-GCM).
 *
 * 1. Generate a random DEK (Data Encryption Key)
 * 2. Encrypt plaintext with DEK
 * 3. Wrap (encrypt) the DEK with the master key
 * 4. Return all components as a prefixed string
 */
export async function encrypt(masterKeyBase64: string, plaintext: string): Promise<string> {
  const masterKey = await importKey(masterKeyBase64);

  // Generate DEK
  const dek = new Uint8Array(32);
  crypto.getRandomValues(dek);
  const dekKey = await crypto.subtle.importKey('raw', dek, { name: 'AES-GCM' }, true, ['encrypt']);

  // Encrypt plaintext with DEK
  const iv1 = new Uint8Array(12);
  crypto.getRandomValues(iv1);
  const ciphertext = await aesEncrypt(dekKey, iv1, new TextEncoder().encode(plaintext));

  // Wrap DEK with master key
  const iv2 = new Uint8Array(12);
  crypto.getRandomValues(iv2);
  const wrappedDek = await aesEncrypt(masterKey, iv2, dek);

  return `${ENVELOPE_PREFIX}${base64Encode(iv1.buffer)}:${base64Encode(ciphertext)}:${base64Encode(iv2.buffer)}:${base64Encode(wrappedDek)}`;
}

/**
 * Decrypt an envelope-encrypted string.
 *
 * 1. Unwrap the DEK using the master key
 * 2. Decrypt the ciphertext using the DEK
 */
export async function decrypt(masterKeyBase64: string, encrypted: string): Promise<string> {
  if (!encrypted.startsWith(ENVELOPE_PREFIX)) {
    throw new Error('Invalid encrypted format: missing enc:v1: prefix');
  }

  const parts = encrypted.slice(ENVELOPE_PREFIX.length).split(':');
  if (parts.length !== 4) {
    throw new Error('Invalid encrypted format: expected 4 components');
  }

  const [iv1B64, ciphertextB64, iv2B64, wrappedDekB64] = parts as [string, string, string, string];
  const iv1 = base64Decode(iv1B64);
  const ciphertext = base64Decode(ciphertextB64);
  const iv2 = base64Decode(iv2B64);
  const wrappedDek = base64Decode(wrappedDekB64);

  const masterKey = await importKey(masterKeyBase64);

  // Unwrap DEK
  const dekBytes = await aesDecrypt(masterKey, iv2, wrappedDek);
  const dekKey = await crypto.subtle.importKey('raw', dekBytes, { name: 'AES-GCM' }, false, ['decrypt']);

  // Decrypt ciphertext
  const plainBytes = await aesDecrypt(dekKey, iv1, ciphertext);
  return new TextDecoder().decode(plainBytes);
}

/**
 * Check whether a value carries the envelope's prefix: an envelope, or a reference
 * (`CREDENTIAL_REFERENCE_PREFIX`), which carries it on purpose and which `decrypt` refuses.
 */
export function isEncrypted(value: string): boolean {
  return value.startsWith(ENVELOPE_PREFIX);
}

/**
 * The stored form of a credential field held outside this Worker: `enc:v1:vault:<id>`, a reference
 * in the field's place, so a field holding one is never null (wayai-pro/wayai#5644). `<id>` holds no
 * colon. It wears the envelope's prefix on purpose: a reader takes it for ciphertext and `decrypt`
 * refuses it (two components, not four), so a reference in a field the reader decrypts fails closed
 * on code of any age, this Worker's or the data tree's, a rollback past `referencedCredentialField`
 * included. Only that helper's readers also refuse one in a field they would not decrypt: on older
 * code that field reads as absent, so a reference may only land in such a field once no rollback
 * can reach older code. A real envelope's first component is a 12-byte IV in base64, so it can
 * never read `vault`.
 */
const CREDENTIAL_REFERENCE_PREFIX = `${ENVELOPE_PREFIX}vault:`;

/** Whether a stored credential value is a reference (`CREDENTIAL_REFERENCE_PREFIX`), not ciphertext. */
export function isCredentialReference(value: unknown): boolean {
  return typeof value === 'string' && value.startsWith(CREDENTIAL_REFERENCE_PREFIX);
}

/** The `<id>` of a stored reference, unchecked beyond its prefix; null for anything else. */
export function credentialReferenceId(value: unknown): string | null {
  return isCredentialReference(value) ? (value as string).slice(CREDENTIAL_REFERENCE_PREFIX.length) : null;
}

/**
 * The first field of a stored credential blob that holds a reference, whichever field it is: a
 * reader that visits only the fields it knows would answer one elsewhere as absent, which reads as
 * "no auth". Null when none does, or the blob is no object. `username` is never one: the blob's one
 * plaintext field, stored as typed, so a tenant can type this form there and it stays a username.
 */
export function referencedCredentialField(storedCredentials: unknown): string | null {
  if (typeof storedCredentials !== 'object' || storedCredentials === null) return null;
  for (const [field, value] of Object.entries(storedCredentials)) {
    if (field !== 'username' && isCredentialReference(value)) return field;
  }
  return null;
}

/**
 * HMAC-SHA-256 over UTF-8 strings, lowercase-hex output.
 *
 * Used for keyed, context-bound credential fingerprints (the CMA harness
 * provider-resource cache): the driver fingerprints a connection secret as
 * `hmacSha256Hex(HARNESS_FINGERPRINT_KEY, "v1\n<hub_id>\n<connection_id>\n<secret>")`
 * so cached provider ids can be invalidated on credential rotation without ever
 * storing the secret. The key is a dedicated Worker secret — deliberately NOT
 * MASTER_KEY, so master-key rotation does not invalidate every fingerprint.
 *
 * RN-safe: crypto.subtle only, no Node imports (this package is React-Native-safe).
 */
export async function hmacSha256Hex(key: string, message: string): Promise<string> {
  const encoder = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = new Uint8Array(await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message)));
  return Array.from(signature).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Constant-time string comparison to prevent timing attacks.
 *
 * On length mismatch, compares `a` against itself to maintain constant time
 * (avoids leaking length information through early return).
 */
export function timingSafeEqual(a: string, b: string): boolean {
  const lengthMatch = a.length === b.length;
  if (!lengthMatch) {
    b = a;
  }

  let result = lengthMatch ? 0 : 1;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

export function timingSafeEqualBytes(a: Uint8Array, b: Uint8Array): boolean {
  const lengthMatch = a.length === b.length;
  if (!lengthMatch) {
    b = a; // avoid early-return timing oracle on length mismatch
  }

  let result = lengthMatch ? 0 : 1;
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i];
  }
  return result === 0;
}
